import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ApiError } from "@njbsoft/api-client";
import {
  Breadcrumbs,
  Card,
  EmptyState,
  EmptyValue,
  PageHeader,
  PageSkeleton,
} from "@/components/ui";
import { useToast } from "@/hooks/use-toast";
import { useActiveMembership, useMyCompanyContext } from "@/hooks/use-company-context";
import { useIsCompanyAdmin } from "@/hooks/use-company-role";
import { useCompanyMember } from "@/hooks/use-company-member";
import { useUpdateCompanyMemberRole } from "@/hooks/use-company-member-role";
import { RoleChangeControl } from "@/components/roster/role-change-control";
import { MemberCredentialsCard } from "@/components/roster/member-credentials-card";
import { MemberKitsCard } from "@/components/roster/member-kits-card";
import { MemberAuthorizationsCard } from "@/components/roster/member-authorizations-card";
import { MemberSubmissionsCard } from "@/components/roster/member-submissions-card";
import { formatDateTime } from "@/lib/dates";

export const Route = createFileRoute("/_company/company/roster/$memberId")({
  component: MemberDetailPage,
});

function MemberDetailPage() {
  const { t } = useTranslation(["company", "common"]);
  const navigate = useNavigate();
  const { memberId } = Route.useParams();
  const active = useActiveMembership();
  const isAdmin = useIsCompanyAdmin();
  const companyId = active?.company_id ?? "";
  const toasts = useToast();

  const context = useMyCompanyContext();
  const selfProfileId = context.data?.profile.id ?? "";

  const [roleRefusal, setRoleRefusal] = useState<string | null>(null);

  const memberQuery = useCompanyMember(companyId, memberId);
  const roleMutation = useUpdateCompanyMemberRole(companyId, memberId);

  const handleRoleChange = (next: "admin" | "tester") => {
    setRoleRefusal(null);
    roleMutation.mutate(next, {
      onSuccess: () => toasts.success("company:roster.member.roleChanged"),
      onError: (err) => {
        const code = err instanceof ApiError ? err.code : undefined;
        // The last-admin refusal is a verdict about this company's admin count.
        // It renders beside the control, and because that control is driven by
        // the role the SERVER last reported, the row snaps straight back.
        if (code === "company_last_admin") {
          setRoleRefusal(t("company:roster.member.lastAdminRole"));
          return;
        }
        toasts.apiError(code);
      },
    });
  };

  if (!active) return null;

  if (memberQuery.isLoading) {
    return (
      <div className="p-8">
        <PageSkeleton />
      </div>
    );
  }

  const detail = memberQuery.data;
  if (memberQuery.isError || !detail) {
    return (
      <div className="space-y-6 p-8">
        <Breadcrumbs items={[{ label: t("company:roster.title"), to: "/company/roster" }]} />
        <EmptyState
          title={t("company:roster.member.loadFailed")}
          description={t("company:roster.member.loadFailedHint")}
          actionLabel={t("company:roster.title")}
          onAction={() => void navigate({ to: "/company/roster" })}
        />
      </div>
    );
  }

  const member = detail.member;
  const displayName = member.full_name ?? member.email ?? member.profile_id;
  // The holder half of the row-owner rule: the viewer IS this member. An
  // unresolved profile id makes nobody self, which withholds an affordance
  // rather than offering one on a stranger's rows.
  const isSelf = selfProfileId !== "" && member.profile_id === selfProfileId;

  return (
    <div className="space-y-6 p-8">
      <Breadcrumbs
        items={[
          { label: t("company:roster.title"), to: "/company/roster" },
          { label: displayName },
        ]}
      />
      <PageHeader
        title={displayName}
        subtitle={t("company:roster.member.subtitle", { company: active.name })}
      />

      <Card title={t("company:roster.member.identity")}>
        <dl className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-muted">{t("company:roster.member.fields.email")}</dt>
            <dd className="text-on-surface">{member.email ?? <EmptyValue />}</dd>
          </div>
          <div>
            <dt className="text-muted">{t("company:roster.member.fields.title")}</dt>
            <dd className="text-on-surface">{member.title ?? <EmptyValue />}</dd>
          </div>
          <div>
            <dt className="text-muted">{t("company:roster.member.fields.joined")}</dt>
            <dd className="text-on-surface">{formatDateTime(member.created_at)}</dd>
          </div>
          <div>
            <dt className="text-muted">{t("company:roster.member.fields.gaugesHeld")}</dt>
            <dd className="tabular-nums text-on-surface">{member.kits_held}</dd>
          </div>
          <div className="sm:col-span-2">
            <dt className="text-muted">{t("company:roster.member.fields.role")}</dt>
            <dd className="mt-1">
              <RoleChangeControl
                role={member.role}
                canEdit={isAdmin}
                isPending={roleMutation.isPending}
                refusalMessage={roleRefusal ?? undefined}
                onChange={handleRoleChange}
              />
            </dd>
          </div>
        </dl>
      </Card>

      <MemberCredentialsCard
        companyId={companyId}
        profileId={member.profile_id}
        isAdmin={isAdmin}
        isSelf={isSelf}
      />

      <MemberKitsCard companyId={companyId} profileId={member.profile_id} />

      <MemberAuthorizationsCard authorizations={detail.authorizations} />

      <MemberSubmissionsCard companyId={companyId} profileId={member.profile_id} />
    </div>
  );
}
